import { getQueryKey } from '@trpc/react-query';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';
import useOrderedCustomRequests from '../useOrderedCustomRequests';

export default function useCustomKycRequests(vehicleId: string) {
  const queryKey = getQueryKey(
    api.onboardingCustomRequest.listKycCustomRequests,
    { vehicleId },
    'query',
  );

  const { data: customKycRequests, isLoading: isLoadingCustomKycRequests } =
    api.onboardingCustomRequest.listKycCustomRequests.useQuery({
      vehicleId,
    });

  const { orderedCustomRequests, setCustomRequestsOrder } =
    useOrderedCustomRequests(customKycRequests ?? []);

  const { mutate: addRequest, isPending: isAddingRequest } =
    api.onboardingCustomRequest.addKycCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request added successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: updateRequest, isPending: isUpdatingRequest } =
    api.onboardingCustomRequest.updateKycCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request updated successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: deleteRequest, isPending: isDeletingRequest } =
    api.onboardingCustomRequest.deleteKycCustomRequest.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Request deleted successfully.',
        invalidations: [queryKey],
      }),
    );

  const { mutate: reorderKycCustomRequests } =
    api.onboardingCustomRequest.reorderKycCustomRequests.useMutation(
      useHandleTrpcMutation()({
        invalidations: [queryKey],
        onSuccess: () => {
          setCustomRequestsOrder([]);
        },
      }),
    );

  const handleCustomRequestsOrderChange = (orderedItemIds: Array<string>) => {
    setCustomRequestsOrder(orderedItemIds);
    reorderKycCustomRequests({
      vehicleId,
      orderedItemIds,
    });
  };

  const isLoading =
    isLoadingCustomKycRequests ||
    isAddingRequest ||
    isUpdatingRequest ||
    isDeletingRequest;

  return {
    customKycRequests: orderedCustomRequests,
    handleCustomRequestsOrderChange,
    addCustomKycRequest: addRequest,
    updateCustomKycRequest: updateRequest,
    deleteCustomKycRequest: deleteRequest,
    isLoading,
  };
}
